import React, { useState } from 'react';
import ServiceLayout from '../components/ServiceLayout.jsx';
import { Quote, Plus, X, Phone, Calendar, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const FAQAccordion = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <div key={index} className="bg-white rounded-xl border border-[#E8DDCE] overflow-hidden shadow-sm">
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full p-6 flex justify-between items-center text-left group cursor-pointer"
          >
            <h3 className="text-lg font-bold text-[#123F13]">
              {item.question}
            </h3>
            <div className="text-[#8C2F12] transition-transform duration-300">
              {openIndex === index ? <X size={20} /> : <Plus size={20} />}
            </div>
          </button>
          <AnimatePresence>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <div className="px-6 pb-6 text-[#3B352F]/70 leading-relaxed">
                  {item.answer}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const Panikangst = () => {
  const canonicalUrl = "https://corehypnose.dk/panikangst";
  
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Behandling af Panikangst i København",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Core Hypnose v/ Lars Nissen Corell – Psykoterapeut MPF",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Valby Tingsted 7",
        "addressLocality": "Valby",
        "postalCode": "2500",
        "addressCountry": "DK"
      },
      "telephone": "+4527204671"
    },
    "description": "Effektiv behandling af panikangst med metakognitiv terapi og hypnose i Valby og online hos Lars Nissen Corell – Psykoterapeut MPF. Genfind roen og kontrollen over din krop.",
    "areaServed": "Danmark",
    "serviceType": "Psykoterapi"
  };

  return (
    <ServiceLayout 
      title="Panikangstbehandling i København"
      subtitle="Panikangst føles som at miste kontrollen. Metakognitiv terapi kombineret med hypnose hjælper dig med at genvinde kontrollen og få ro i krop og sind."
      image="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/e8e5e3e6-2dd5-476d-af1c-d64cc6fe3955.png"
      description="Hos CORE HYPNOSE tilbydes en effektiv og skånsom tilgang til panikangst. Slip frygten, få styr på triggertanker og oplev mere frihed i hverdagen."
      actions={
        <>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#193f10] text-white px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#193f10]/90 transition-all shadow-md shrink-0">
            <Calendar size={18} /> Book første behandlingssession
          </Link>
          <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#193f10] text-[#193f10] px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#faf7f2] transition-all shrink-0">
            <Phone size={18} /> Tlf. 27 20 46 71
          </a>
        </>
      }
      footer={
        <div className="text-center pb-16">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-8">Klar til at få ro og kontrol igen?</h2>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#a33a1a] transition-all shadow-lg">
            Book din første session nu <ArrowRight size={20} />
          </Link>
        </div>
      }
    >
      <Helmet>
        <title>Panikangst behandling | Meta Corehypnose | København</title>
        <meta name="description" content="Panikangst er en udfordrende og invaliderende tilstand, som kan behandles effektivt med metakognitiv hypnose terapi. Læs om behandlingen her ➜" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Panikangst behandling | Meta Corehypnose | København" />
        <meta property="og:description" content="Panikangst er en udfordrende og invaliderende tilstand, som kan behandles effektivt med metakognitiv hypnose terapi. Læs om behandlingen her ➜" />
        <meta property="og:image" content="" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="" />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* Testimonial Section */}
      <div className="bg-[#123F13] text-white p-8 md:p-12 rounded-2xl relative overflow-hidden mb-16">
        <Quote className="absolute top-8 right-8 text-white/10" size={120} />
        <div className="relative z-10 max-w-2xl">
          <h3 className="text-2xl font-serif mb-6 italic">“Det var som om, jeg kunne trække vejret frit igen”</h3>
          <p className="text-white/80 mb-6 leading-relaxed">
            Karen oplevede pludselige panikanfald med svimmelhed og frygt for at dø. Hun havde isoleret sig og mistet modet på at få det bedre.
            Efter få sessioner begyndte Karen at føle sig mere tryg. Hun lærte, hvordan hun kunne lade triggertankerne være og fokusere på nuet.
          </p>
          <p className="font-bold">— Karen, 53 år</p>
          <p className="text-white/60 text-sm mt-4 italic">Karen genfandt roen og modet til at gå ud i livet igen.</p>
        </div>
      </div>

      {/* Hvad er panikangst sektion */}
      <section className="mb-16">
        <div className="w-full">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">Hvad er panikangst?</h2>
          <div className="space-y-4 text-[#3B352F]/70 leading-relaxed text-lg">
            <p>
              Panikangst er gentagne anfald af intens angst og kropslige reaktioner, som kan føles som hjertebanken, åndedrætsbesvær, svimmelhed og en frygt for at miste kontrollen eller dø.
            </p>
            <p>
              Anfaldene varer typisk 5–20 minutter, men følelsen af frygt for “næste anfald” (forventningsangst) kan være konstant og dræne din energi. Kroppen reagerer voldsomt, selvom der ikke er nogen reel fare. Det kan komme pludseligt, og det kan føles skræmmende og uforudsigeligt.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
              <div className="bg-[#FAF7F2] p-6 rounded-xl border border-[#E8DDCE]">
                <h3 className="text-xl font-bold text-[#123F13] mb-4">Typiske kropslige reaktioner</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#8C2F12]" /> Hjertebanken og åndedrætsbesvær</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#8C2F12]" /> Svimmelhed og uvirkelighedsfølelse</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#8C2F12]" /> Trykken for brystet og svedeture</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#8C2F12]" /> Frygt for at miste kontrollen</li>
                </ul>
              </div>
              <div className="bg-[#FAF7F2] p-6 rounded-xl border border-[#E8DDCE]">
                <h3 className="text-xl font-bold text-[#123F13] mb-4">Typiske triggertanker</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#8C2F12]" /> Hvad nu hvis jeg besvimer?</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#8C2F12]" /> Jeg kan ikke trække vejret!</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#8C2F12]" /> Jeg mister kontrollen!</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#8C2F12]" /> Panikken stopper aldrig!</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hvorfor bliver det ved sektion */}
      <section className="bg-[#FAF7F2] p-8 md:p-12 rounded-2xl border border-[#E8DDCE] mb-16">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-serif text-[#123F13] mb-6">Hvorfor bliver panikangsten ved?</h2>
          <div className="space-y-4 text-[#3B352F]/70 leading-relaxed text-lg">
            <p>
              Angst føles som en advarsel, og naturligt nok begynder vi at overvåge kroppen, scanne efter symptomer, analysere, undgå situationer og forsøge at få styr på tankerne. Men det fastholder kun angsten.
            </p>
            <p>
              Metakognitiv terapi og hypnose hjælper dig med at bryde cirklen. Du lærer at give mindre opmærksomhed til triggertanker og stopper med at bruge energien på selvovervågning og bekymring.
            </p>
          </div>
        </div>
      </section>

      {/* Hvordan foregår behandlingen sektion */}
      <section className="py-16 border-t border-[#E8DDCE]">
        <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-12">Hvordan foregår behandlingen?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            { title: "Forstå panikangsten", content: "Vi starter med at skabe tryghed og forståelse for, hvad der sker i krop og tanker." },
            { title: "Metakognitive teknikker", content: "Du får konkrete værktøjer til at stoppe overfokusering og overtænkning." },
            { title: "Hypnose", content: "Hypnose hjælper dig med at slippe fysiske symptomer og finde ro på et dybt plan." },
            { title: "Træning og opgaver", content: "Du får enkle opgaver og redskaber, der hjælper dig videre i hverdagen." },
            { title: "Fasthold forandringen", content: "Vi arbejder med forebyggelse af tilbagefald og vedligeholdelse af den nye frihed." }
          ].map((step, i) => (
            <div key={i} className="bg-white p-6 rounded-xl border border-[#E8DDCE] shadow-sm">
              <div className="w-10 h-10 bg-[#123F13] text-white rounded-full flex items-center justify-center font-bold mb-4">
                {i + 1}
              </div>
              <h3 className="text-xl font-bold text-[#123F13] mb-2">{step.title}</h3>
              <p className="text-[#3B352F]/70 leading-relaxed">{step.content}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Hvorfor det virker sektion */}
      <section className="py-16 border-t border-[#E8DDCE]">
        <div className="w-full">
          <h2 className="text-3xl font-serif text-[#123F13] mb-6">Hvorfor min behandling mod panikangst virker</h2>
          <div className="space-y-4 text-[#3B352F]/70 leading-relaxed text-lg">
            <p>
              Hos CoreHypnose handler det ikke om lange forløb, tunge analyser eller at grave i fortiden. Jeg fokuserer på det, du mærker og tænker lige nu, og hvordan du kan håndtere det mere hensigtsmæssigt.
            </p>
            <p>
              Metakognitiv terapi er en enkel, men dybdegående metode, som hjælper dig med at stoppe de mentale strategier, der fastholder angsten. Hypnose skaber ro og kontakt til din krop, og styrker din evne til at regulere dig selv i pressede situationer.
            </p>
            <p className="font-medium text-[#123F13]">
              Du behøver ikke forstå alt, eller lykkes med det hele fra dag et. Du skal bare mærke, at forandring er mulig. Og det begynder ofte allerede i første session.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <div className="pt-16 pb-16 border-t border-[#E8DDCE]">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">FAQ - svar på dine spørgsmål</h2>
        </div>
        <div className="max-w-4xl mx-auto">
          <FAQAccordion items={[
            {
              question: "Hvor mange sessioner har jeg brug for?",
              answer: "Det varierer, men mange mærker en betydelig forskel efter 3-5 sessioner. Nogle har brug for færre, andre for et længere forløb afhængig af angstens karakter."
            },
            {
              question: "Hvordan foregår en behandling?",
              answer: "Jeg kombinerer samtale, metakognitive teknikker og hypnose. Jeg starter med at tale om dine udfordringer, hvorefter jeg arbejder med konkrete redskaber og afslutter ofte med en beroligende hypnose-session."
            },
            {
              question: "Kan jeg bruge behandlingen, hvis jeg har både panikangst og generaliseret angst?",
              answer: "Ja, absolut. Metoderne er yderst effektive over for både specifikke angstformer som panikangst og mere diffuse former som generaliseret angst."
            },
            {
              question: "Hvad koster det?",
              answer: "Se de aktuelle priser på min prisside. Jeg tilbyder også introtilbud til nye klienter."
            },
            {
              question: "Kan jeg få behandling online?",
              answer: "Ja, jeg tilbyder fuldt ud effektive online-sessioner via en sikker forbindelse, så du kan modtage behandling i trygge rammer hjemmefra."
            },
            {
              question: "Er hypnose farligt?",
              answer: "Nej, hypnose er en helt naturlig tilstand af fokuseret opmærksomhed. Du er altid i kontrol og kan ikke bringes til at gøre noget mod din vilje."
            }
          ]} />
        </div>
      </div>

      {/* Related Section */}
      <section className="py-16 border-t border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-12">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Måske er dette også relevant for dig</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
              Læs videre i det, der passer til din situation
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/helbredsangst" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
              <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Helbredsangst og frygt for sygdom</h3>
              <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
            </Link>
            <Link to="/generaliseret-angst" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
              <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Generaliseret angst og bekymring</h3>
              <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
            </Link>
            <Link to="/social-angst" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
              <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Social angst og selvovervågning</h3>
              <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
            </Link>
            <Link to="/metakognitiv-terapi" className="bg-white border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
              <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Metakognitiv terapi</h3>
              <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
            </Link>
          </div>
        </div>
      </section>
    </ServiceLayout>
  );
};

export default Panikangst;
